import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { apiService, PayrollParseResult, PayrollRow } from '../services/api';
import { formatDate } from '../lib/utils';

type Step = 'upload' | 'preview' | 'done';

export function Payroll() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsed, setParsed] = useState<PayrollParseResult | null>(null);
  const [createdBatch, setCreatedBatch] = useState<{ batchId: number; batchReference: string; skipped: string[] } | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setLoading(true);
    try {
      const fileContent = await file.text();
      const result = await apiService.parsePayrollFile(fileContent);
      setParsed(result);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  async function handleConfirm() {
    if (!parsed) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.confirmPayrollBatch({
        rows: parsed.rows,
        paymentDate: parsed.paymentDate,
        currency: parsed.currency,
      });
      const blob = new Blob([result.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
      setCreatedBatch(result);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create batch');
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setParsed(null);
    setCreatedBatch(null);
    setError(null);
    setStep('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // Excludes rows that will be skipped for an unformattable account, so this
  // matches what the batch will actually pay out.
  const totalAmount = parsed?.rows.reduce((s, r) => s + (r.citiAccountFormat ? r.amount : 0), 0) ?? 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Payroll</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload a BGC .IN file to create a CITI153 payroll batch</p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {step === 'upload' && (
          <div
            className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-foreground/40 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".IN,.in,*"
              className="hidden"
              onChange={handleFileChange}
            />
            {loading ? (
              <p className="text-sm text-muted-foreground">Parsing file...</p>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">Drop .IN file here or click to browse</p>
                <p className="text-xs text-muted-foreground mt-1">BGC salary file (Lönefil)</p>
              </>
            )}
          </div>
        )}

        {step === 'preview' && parsed && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg border border-border px-4 py-3">
                <p className="text-xs text-muted-foreground">Payment date</p>
                <p className="text-sm font-medium mt-0.5">{formatDate(parsed.paymentDate)}</p>
              </div>
              <div className="bg-white rounded-lg border border-border px-4 py-3">
                <p className="text-xs text-muted-foreground">Transactions</p>
                <p className="text-sm font-medium mt-0.5">{parsed.rows.length}</p>
              </div>
              <div className="bg-white rounded-lg border border-border px-4 py-3">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-sm font-medium mt-0.5">
                  {totalAmount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })} {parsed.currency}
                </p>
              </div>
            </div>

            {parsed.unknownEmployees.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                {parsed.unknownEmployees.length} employee(s) not found in the employee directory. Personnummer will be used as name.{' '}
                <span className="font-mono text-xs">{parsed.unknownEmployees.join(', ')}</span>
              </div>
            )}

            {parsed.missingBankInfo.length > 0 && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {parsed.missingBankInfo.length} employee(s) missing a synced bank account/clearing number from Fortnox. Batch creation is blocked until this is resolved.{' '}
                <span className="font-mono text-xs">{parsed.missingBankInfo.join(', ')}</span>
              </div>
            )}

            {parsed.invalidBankInfo.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
                <span className="font-medium">{parsed.invalidBankInfo.length} employee(s) will be SKIPPED</span> — clearing/account number can't be formatted for Citi (non-numeric, or account longer than 12 digits — IBAN is not accepted). They will not be included or paid in this batch; resolve their bank info in Fortnox and run them separately.{' '}
                <span className="font-mono text-xs">{parsed.invalidBankInfo.join(', ')}</span>
              </div>
            )}

            <div className="bg-white rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Employee</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Personnummer</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Account</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Citi format</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Value date</th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {parsed.rows.map((row, i) => {
                    const willBeSkipped = !row.citiAccountFormat;
                    return (
                      <tr key={i} className={`hover:bg-muted/20 ${willBeSkipped ? 'bg-amber-50/60' : ''}`}>
                        <td className="px-4 py-2.5">
                          {row.employeeName ?? (
                            <span className="text-amber-600 font-mono text-xs">{row.personnummer}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{row.personnummer}</td>
                        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{row.account}</td>
                        <td className="px-4 py-2.5 font-mono text-xs">
                          {row.citiAccountFormat ?? (
                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                              Skipped — invalid format
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">{formatDate(row.valueDate)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {row.amount.toLocaleString('sv-SE', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleConfirm}
                disabled={loading || parsed.missingBankInfo.length > 0 || parsed.unknownEmployees.length > 0}
                title={
                  parsed.missingBankInfo.length > 0
                    ? 'Resolve missing bank account/clearing numbers before creating the batch'
                    : parsed.unknownEmployees.length > 0
                      ? 'Add or sync the unknown employee(s) before creating the batch'
                      : parsed.invalidBankInfo.length > 0
                        ? `${parsed.invalidBankInfo.length} employee(s) with an invalid account format will be skipped`
                        : undefined
                }
                className="px-4 py-2 text-sm font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 disabled:opacity-50 transition-colors"
              >
                {loading
                  ? 'Creating batch...'
                  : parsed.invalidBankInfo.length > 0
                    ? `Create batch (skipping ${parsed.invalidBankInfo.length})`
                    : 'Create batch'}
              </button>
              <button
                onClick={handleReset}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {step === 'done' && createdBatch && (
          <div className="bg-white rounded-lg border border-border px-6 py-8 text-center space-y-4">
            <p className="text-sm font-medium text-foreground">Batch created successfully</p>
            <p className="text-xs text-muted-foreground font-mono">{createdBatch.batchReference}</p>
            {createdBatch.skipped.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 text-left">
                <span className="font-medium">{createdBatch.skipped.length} employee(s) were skipped and not paid</span> — invalid clearing/account format. Resolve their bank info in Fortnox and run them in a separate batch.{' '}
                <span className="font-mono text-xs">{createdBatch.skipped.join(', ')}</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                onClick={() => navigate('/payments')}
                className="px-4 py-2 text-sm font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors"
              >
                Go to Payments
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Upload another file
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
